import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const FREE_SHIPPING_THRESHOLD = 799;

export function StoreProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vult_cart") || "[]"); } catch { return []; }
  });
  const [coupon, setCoupon] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    localStorage.setItem("vult_cart", JSON.stringify(cart));
  }, [cart]);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const goToCategory = (cat) => {
    setCategoryFilter(cat);
    document.querySelector("#catalogo")?.scrollIntoView({ behavior: "smooth" });
  };

  const addToCart = (product, size, color = null, qty = 1) => {
    setCart((prev) => {
      const key = `${product.id}-${size}-${color || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...prev, {
        key, product_id: product.id, name: product.name, brand: product.brand,
        image: product.image, price: product.price, size, color: color || "", qty,
      }];
    });
    toast.success("Adicionado ao carrinho", { description: `${product.name} — Tam ${size}${color ? ` • ${color}` : ""}` });
  };

  const updateQty = (key, delta) => {
    setCart((prev) =>
      prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, i.qty + delta) } : i))
    );
  };

  const removeItem = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => { setCart([]); setCoupon(null); };

  const applyCoupon = async (code) => {
    try {
      const { data } = await api.get(`/coupons/validate/${encodeURIComponent(code.trim().toUpperCase())}`);
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado: ${data.percent}% OFF`);
      return true;
    } catch {
      toast.error("Cupom inválido ou expirado");
      return false;
    }
  };

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const discount = coupon ? subtotal * (coupon.percent / 100) : 0;
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <StoreContext.Provider value={{
      products, loading, fetchProducts,
      cart, addToCart, updateQty, removeItem, clearCart,
      subtotal, discount, cartCount, coupon, applyCoupon,
      cartOpen, setCartOpen, checkoutOpen, setCheckoutOpen,
      selected, setSelected, categoryFilter, setCategoryFilter, goToCategory,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
